import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import classes from "./blog.module.css";
import Footer from "../../components/footer";
import { blogPosts } from "../../data/blogPosts";

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPosts, setFilteredPosts] = useState(blogPosts);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(9);
  const [currentPosts, setCurrentPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(
    Math.ceil(blogPosts.length / postsPerPage)
  );

  // Handle search input
  const handleSearch = (e) => {
    e.preventDefault();
    const filtered = blogPosts.filter(
      (post) =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.content.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPosts(filtered);
    setCurrentPage(1); // Reset to first page when searching
    setTotalPages(Math.ceil(filtered.length / postsPerPage));
  };

  // Reset search results
  const resetSearch = () => {
    setSearchTerm("");
    setFilteredPosts(blogPosts);
    setCurrentPage(1); // Reset to first page
    setTotalPages(Math.ceil(blogPosts.length / postsPerPage));
  };

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Go to next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Go to previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Update current posts whenever filtered posts or current page changes
  useEffect(() => {
    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    setCurrentPosts(filteredPosts.slice(indexOfFirstPost, indexOfLastPost));
  }, [filteredPosts, currentPage, postsPerPage]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Pagination navigation component
  const PaginationNav = () => {
    const pageNumbers = [];

    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }

    // Create a simpler pagination interface when there are many pages
    const renderPageNumbers = () => {
      if (totalPages <= 7) {
        // Show all page numbers if 7 or fewer
        return pageNumbers.map((number) => (
          <button
            key={number}
            onClick={() => paginate(number)}
            className={`${classes.pageLink} ${
              currentPage === number ? classes.activePage : ""
            }`}
          >
            {number}
          </button>
        ));
      } else {
        // Show limited page numbers with ellipsis
        const pages = [];

        // Always show first page
        pages.push(
          <button
            key={1}
            onClick={() => paginate(1)}
            className={`${classes.pageLink} ${
              currentPage === 1 ? classes.activePage : ""
            }`}
          >
            1
          </button>
        );

        // Show ellipsis if not near the beginning
        if (currentPage > 3) {
          pages.push(
            <span key="ellipsis1" className={classes.ellipsis}>
              ...
            </span>
          );
        }

        // Show current page and neighbors
        const startPage = Math.max(2, currentPage - 1);
        const endPage = Math.min(totalPages - 1, currentPage + 1);

        for (let i = startPage; i <= endPage; i++) {
          if (i > 1 && i < totalPages) {
            pages.push(
              <button
                key={i}
                onClick={() => paginate(i)}
                className={`${classes.pageLink} ${
                  currentPage === i ? classes.activePage : ""
                }`}
              >
                {i}
              </button>
            );
          }
        }

        // Show ellipsis if not near the end
        if (currentPage < totalPages - 2) {
          pages.push(
            <span key="ellipsis2" className={classes.ellipsis}>
              ...
            </span>
          );
        }

        // Always show last page
        if (totalPages > 1) {
          pages.push(
            <button
              key={totalPages}
              onClick={() => paginate(totalPages)}
              className={`${classes.pageLink} ${
                currentPage === totalPages ? classes.activePage : ""
              }`}
            >
              {totalPages}
            </button>
          );
        }

        return pages;
      }
    };

    return (
      <div className={classes.pagination}>
        <button
          onClick={prevPage}
          disabled={currentPage === 1}
          className={`${classes.pageNav} ${
            currentPage === 1 ? classes.pageNavDisabled : ""
          }`}
        >
          ← Previous
        </button>

        <div className={classes.pageNumbers}>{renderPageNumbers()}</div>

        <button
          onClick={nextPage}
          disabled={currentPage === totalPages || totalPages === 0}
          className={`${classes.pageNav} ${
            currentPage === totalPages || totalPages === 0
              ? classes.pageNavDisabled
              : ""
          }`}
        >
          Next →
        </button>
      </div>
    );
  };

  return (
    <>
      <div className={classes.blogContainer}>
        {/* Hero Section */}
        <div className={classes.heroSection}>
          <div className={classes.heroContent}>
            <div className={classes.logo}>🎨</div>
            <h1 className={classes.title}>Welcome to ArtistNextDoor Blog</h1>
            <p className={classes.subtitle}>
              Connecting with local artists and art enthusiasts in your
              community. Discover talented creators, upcoming events, and
              creative inspiration.
            </p>
            <form className={classes.searchContainer} onSubmit={handleSearch}>
              <input
                type="text"
                placeholder="Search Articles"
                className={classes.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="submit" className={classes.searchButton}>
                Search
              </button>
              {searchTerm && (
                <button
                  type="button"
                  className={classes.resetButton}
                  onClick={resetSearch}
                >
                  Reset
                </button>
              )}
            </form>
          </div>
        </div>

        {/* Blog Listings */}
        <div className={classes.contentSection}>
          <div className={classes.sectionHeader}>
            <h2 className={classes.sectionTitle}>
              {searchTerm
                ? `Search Results for "${searchTerm}"`
                : "Latest Articles"}
            </h2>

            {/* Categories Filter - Optional */}
            {/* <div className={classes.categories}>
              <span className={`${classes.category} ${classes.activeCategory}`}>
                All
              </span>
              <span className={classes.category}>Event Planning</span>
              <span className={classes.category}>Artist Resources</span>
              <span className={classes.category}>Entertainment</span>
            </div> */}
          </div>

          {filteredPosts.length > 0 ? (
            <>
              <div className={classes.blogGrid}>
                {currentPosts.map((post) => (
                  <div key={post.id} className={classes.blogCard}>
                    <Link
                      to={`/blog/${post.slug}`}
                      className={classes.blogLink}
                    >
                      <div className={classes.blogImageContainer}>
                        {post.image ? (
                          <img
                            src={post.image}
                            alt={post.title}
                            className={classes.blogImage}
                          />
                        ) : (
                          <div className={classes.blogImagePlaceholder}></div>
                        )}
                      </div>
                      <div className={classes.blogContent}>
                        <h3 className={classes.blogTitle}>{post.title}</h3>
                        <div className={classes.blogMeta}>
                          <span>by {post.author}</span>
                          <span> | </span>
                          <span>{post.date}</span>
                        </div>
                        <p className={classes.blogExcerpt}>{post.excerpt}</p>
                        <span className={classes.readMore}>Read More</span>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <PaginationNav />

              <div className={classes.paginationInfo}>
                Showing {currentPosts.length} of {filteredPosts.length} articles
                {totalPages > 0 && ` • Page ${currentPage} of ${totalPages}`}
              </div>
            </>
          ) : (
            <div className={classes.noResults}>
              <h3>No articles found</h3>
              <p>Try adjusting your search terms or browse our categories.</p>
              <button className={classes.browseAllButton} onClick={resetSearch}>
                Browse All Articles
              </button>
            </div>
          )}
        </div>

        {/* Featured Categories Section - Optional */}
        <div className={classes.categoriesSection}>
          <h2 className={classes.sectionTitle}>Browse by Category</h2>
          <div className={classes.categoryCards}>
            <div className={classes.categoryCard}>
              <div className={classes.categoryIcon}>🎭</div>
              <h3>Event Planning</h3>
              <p>Tips and guides for organizing events with live performers</p>
              <Link
                to="/blog/category/event-planning"
                className={classes.categoryLink}
              >
                Explore Articles
              </Link>
            </div>
            <div className={classes.categoryCard}>
              <div className={classes.categoryIcon}>🎸</div>
              <h3>Artist Resources</h3>
              <p>Helpful resources for artists to grow their career</p>
              <Link
                to="/blog/category/artist-resources"
                className={classes.categoryLink}
              >
                Explore Articles
              </Link>
            </div>
            <div className={classes.categoryCard}>
              <div className={classes.categoryIcon}>🎉</div>
              <h3>Entertainment</h3>
              <p>
                Discover the latest trends in entertainment and performances
              </p>
              <Link
                to="/blog/category/entertainment"
                className={classes.categoryLink}
              >
                Explore Articles
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Blog;
